import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../../utils/authUtils';
import { SHOP_ORDER_STATUSES, formatVnd, shopOrderStatusColor, shopOrderStatusLabel } from '../../constants/shopOrderStatus';
import { resolveImageSrc } from '../../utils/productGallery';
import { PLACEHOLDER_50 } from '../../utils/imagePlaceholder';
import './AdminShopOrders.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const AdminShopOrders = () => {
  const [status, setStatus] = useState('all');
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [nextStatus, setNextStatus] = useState('pending');
  const [adminNote, setAdminNote] = useState('');
  const [message, setMessage] = useState('');
  const [keyword, setKeyword] = useState('');

  const load = async (filter = status) => {
    const query = filter && filter !== 'all' ? `?status=${filter}` : '';
    const response = await fetchWithAuth(`${API_BASE}/api/admin/shop-orders${query}`);
    const data = await response.json();
    if (data.success) setOrders(data.data || []);
    else setMessage(data.message || 'Không tải được đơn');
  };

  useEffect(() => { load('all'); }, []);

  const open = async (id) => {
    if (selected?.id === id) {
      setSelected(null);
      return;
    }
    const response = await fetchWithAuth(`${API_BASE}/api/admin/shop-orders/${id}`);
    const data = await response.json();
    if (!data.success) return;
    setSelected(data.data);
    setNextStatus(data.data.status);
    setAdminNote(data.data.admin_note || '');
  };

  const save = async () => {
    if (!selected) return;
    setMessage('');
    const response = await fetchWithAuth(`${API_BASE}/api/admin/shop-orders/${selected.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: nextStatus, adminNote }),
    });
    const data = await response.json();
    if (!data.success) {
      setMessage(data.message || 'Không cập nhật được');
      return;
    }
    setSelected(data.data);
    setMessage(`Đã chuyển đơn ${data.data.order_code} sang ${shopOrderStatusLabel(data.data.status)}`);
    load();
  };

  const filteredOrders = orders.filter((order) => {
    const q = keyword.trim().toLowerCase();
    if (!q) return true;
    return [order.order_code, order.full_name, order.username, order.receiver_name, order.receiver_phone]
      .join(' ')
      .toLowerCase()
      .includes(q);
  });

  const shippingLabel = selected && Number(selected.shipping_fee) > 0
    ? `₫${formatVnd(selected.shipping_fee)}`
    : 'Miễn phí';

  return (
    <div className="admin-orders">
      <div className="admin-orders-toolbar">
        <label>
          Lọc trạng thái
          <select value={status} onChange={(e) => { setStatus(e.target.value); load(e.target.value); }}>
            <option value="all">Tất cả</option>
            {SHOP_ORDER_STATUSES.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className="admin-shop-search">
          Tìm đơn
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Mã đơn, tên khách, số điện thoại"
          />
        </label>
        <span className="admin-orders-count">{filteredOrders.length} đơn</span>
      </div>
      {message ? <p className="admin-orders-msg">{message}</p> : null}
      <div className="admin-orders-layout">
        <div className="admin-orders-list">
          <table>
            <thead>
              <tr>
                <th>Mã</th>
                <th>Khách</th>
                <th>Tổng</th>
                <th>Trạng thái</th>
                <th>Ngày</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} onClick={() => open(order.id)} className={selected?.id === order.id ? 'active' : ''}>
                  <td className="col-code">{order.order_code}</td>
                  <td>{order.full_name || order.username}</td>
                  <td className="col-money">₫{formatVnd(order.total_vnd)}</td>
                  <td>
                    <span className="order-status-pill" style={{ background: shopOrderStatusColor(order.status) }}>
                      {shopOrderStatusLabel(order.status)}
                    </span>
                  </td>
                  <td className="col-date">{new Date(order.created_at).toLocaleString('vi-VN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="admin-orders-panel">
          {!selected ? (
            <div className="admin-orders-panel-empty">
              <p>Chọn một đơn để cập nhật trạng thái.</p>
            </div>
          ) : (
            <div className="admin-orders-detail">
              <header className="order-detail-header">
                <div>
                  <span className="order-detail-label">Mã đơn</span>
                  <h3 className="order-detail-code">{selected.order_code}</h3>
                </div>
                <span
                  className="order-status-pill order-status-pill--lg"
                  style={{ background: shopOrderStatusColor(selected.status) }}
                >
                  {shopOrderStatusLabel(selected.status)}
                </span>
              </header>

              <section className="order-detail-block">
                <h4 className="order-detail-block-title">Người nhận</h4>
                <dl className="order-detail-dl">
                  <div>
                    <dt>Họ tên</dt>
                    <dd>{selected.receiver_name}</dd>
                  </div>
                  <div>
                    <dt>Điện thoại</dt>
                    <dd className="order-detail-phone">{selected.receiver_phone}</dd>
                  </div>
                  <div>
                    <dt>Địa chỉ</dt>
                    <dd>{selected.receiver_address}</dd>
                  </div>
                </dl>
              </section>

              <section className="order-detail-block order-detail-block--money">
                <h4 className="order-detail-block-title">Thanh toán</h4>
                <div className="order-detail-money-row">
                  <span>Phí ship</span>
                  <strong className={Number(selected.shipping_fee) > 0 ? '' : 'order-detail-free'}>{shippingLabel}</strong>
                </div>
                <div className="order-detail-money-row order-detail-total">
                  <span>Tổng đơn</span>
                  <strong>₫{formatVnd(selected.total_vnd)}</strong>
                </div>
              </section>

              {selected.note ? (
                <section className="order-detail-block order-detail-block--note">
                  <h4 className="order-detail-block-title">Ghi chú khách</h4>
                  <p className="order-detail-customer-note">{selected.note}</p>
                </section>
              ) : null}

              <section className="order-detail-block">
                <h4 className="order-detail-block-title">Sản phẩm ({(selected.items || []).length})</h4>
                <ul className="order-detail-items">
                  {(selected.items || []).map((item) => (
                    <li key={item.id} className="order-detail-item">
                      <img
                        className="order-item-thumb"
                        src={item.image_url ? resolveImageSrc(item.image_url) : PLACEHOLDER_50}
                        alt=""
                        onError={(e) => { e.target.src = PLACEHOLDER_50; }}
                      />
                      <div className="order-item-body">
                        <span className="order-item-name">{item.product_name}</span>
                        <span className="order-item-meta">
                          ₫{formatVnd(item.unit_price)}/sp
                        </span>
                      </div>
                      <span className="order-item-qty">× {item.quantity}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="order-detail-block order-detail-block--actions">
                <h4 className="order-detail-block-title">Cập nhật đơn</h4>
                <label>
                  Trạng thái
                  <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)}>
                    {SHOP_ORDER_STATUSES.map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Ghi chú nội bộ
                  <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} />
                </label>
                <button type="button" className="order-detail-save" onClick={save}>Lưu trạng thái</button>
              </section>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default AdminShopOrders;
